import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UseSupabaseAvailabilityResult {
  unavailableDates: Date[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useSupabaseAvailability = (
  propertyId: string,
): UseSupabaseAvailabilityResult => {
  const {
    data: unavailableDates = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["property-availability", propertyId],
    queryFn: async (): Promise<Date[]> => {
      if (!propertyId) {
        return [];
      }

      const { data: blockedDays, error: blockedError } = await supabase
        .from("property_blocked_days")
        .select("blocked_date")
        .eq("property_id", propertyId)
        .order("blocked_date", { ascending: true });

      if (blockedError) {
        throw blockedError;
      }

      const unavailableDates: Date[] = [];

      blockedDays?.forEach((item) => {
        const dateString = item.blocked_date;

        const [year, month, day] = dateString.split("-").map(Number);
        const localDate = new Date(year, month - 1, day);
        unavailableDates.push(localDate);
      });

      return unavailableDates;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    unavailableDates,
    isLoading,
    error: error ? String(error) : null,
    refetch,
  };
};

export const useSyncPropertyAvailability = () => {
  return async (propertyId: string, icalUrl: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("sync-ical", {
        body: { propertyId, icalUrl },
      });

      if (error) {
        console.error("Error syncing availability:", error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error in sync function:", error);
      throw error;
    }
  };
};
